import React from 'react';

const Terms: React.FC = () => {
  return (
    <div className="w-full overflow-hidden pt-24">
      {/* Terms Hero Section */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] py-16 md:py-24 overflow-hidden">
        <div className="w-full px-0">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
              <span className="text-emerald-700">Terms</span> <span className="text-orange-500">&</span> <span className="text-emerald-700">Conditions</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Understand the rules and guidelines for using MarketMate
            </p>
          </div>

          <div className="w-full">
            <img
              src="/images/Privacy and legal.png"
              alt="Terms and Conditions"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Terms Content Section */}
      <section className="relative w-full bg-[#e8f5f0] py-20 md:py-32 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-orange-500 mb-4">Terms & Conditions</h2>
            <p className="text-slate-600 mb-8 font-semibold">Last updated: April 2026</p>

            <p className="text-slate-700 mb-8">
              Welcome to MarketMate. These Terms & Conditions govern your use of our platform, including our website, mobile app, and services.
            </p>
            <p className="text-slate-700 font-semibold mb-8">
              By using MarketMate, you agree to these terms.
            </p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">1. About MarketMate</h3>
            <p className="text-slate-700 mb-4">
              MarketMate is a platform that connects customers, farmers/vendors, and riders to make it easy to buy, sell, and deliver fresh produce.
            </p>
            <p className="text-slate-700 mb-8">
              We act as a facilitator, not the direct seller of products listed on the platform.
            </p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">2. Using MarketMate</h3>
            <p className="text-slate-700 mb-4">By using our platform, you agree to:</p>
            <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
              <li>Provide accurate and up-to-date information</li>
              <li>Use the platform only for lawful purposes</li>
              <li>Not misuse, disrupt, or attempt to interfere with the system</li>
            </ul>
            <p className="text-slate-700 mb-8">We reserve the right to suspend or terminate accounts that violate these terms.</p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">3. Orders & Deliveries</h3>
            <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
              <li>Orders are placed through the MarketMate platform</li>
              <li>Vendors/farmers are responsible for preparing items</li>
              <li>Riders handle delivery</li>
            </ul>
            <p className="text-slate-700 mb-4">While we aim for timely and accurate delivery:</p>
            <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
              <li>Delivery times may vary based on location and availability</li>
              <li>Delays may occur due to unforeseen circumstances</li>
            </ul>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">4. Payments</h3>
            <ul className="list-disc list-inside text-slate-700 mb-6 space-y-2">
              <li>All payments are made through secure channels (card or bank transfer)</li>
              <li>Orders must be paid for before they are processed</li>
              <li>We do not support pay-on-delivery</li>
            </ul>
            <p className="text-slate-700 mb-8">MarketMate is not responsible for issues caused by third-party payment providers.</p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">5. Pricing & Availability</h3>
            <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
              <li>Prices and availability of items are set by farmers/vendors</li>
              <li>Items may change or become unavailable without notice</li>
              <li>We do our best to ensure accurate listings but cannot guarantee it at all times</li>
            </ul>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">6. Cancellations & Refunds</h3>
            <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
              <li>Orders may only be cancelled within a limited time after placement</li>
              <li>Refunds may be issued in cases such as:
                <ul className="list-circle list-inside ml-4 mt-2 space-y-1">
                  <li>Failed delivery</li>
                  <li>Incorrect or missing items</li>
                  <li>Verified quality issues</li>
                </ul>
              </li>
            </ul>
            <p className="text-slate-700 mb-8">Refunds are processed according to our internal review process.</p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">7. Responsibilities of Users</h3>
            
            <h4 className="text-xl font-semibold text-slate-800 mt-4 mb-2">Customers</h4>
            <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
              <li>Provide correct delivery details</li>
              <li>Be available to receive orders</li>
            </ul>

            <h4 className="text-xl font-semibold text-slate-800 mt-4 mb-2">Farmers / Vendors</h4>
            <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
              <li>Ensure product quality and accurate listings</li>
              <li>Fulfil orders promptly</li>
            </ul>

            <h4 className="text-xl font-semibold text-slate-800 mt-4 mb-2">Riders</h4>
            <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
              <li>Deliver orders safely and on time</li>
              <li>Maintain professionalism during deliveries</li>
            </ul>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">8. Platform Availability</h3>
            <p className="text-slate-700 mb-8">
              We aim to keep MarketMate running smoothly at all times. However, we do not guarantee uninterrupted access and may perform maintenance or updates when necessary.
            </p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">9. Limitation of Liability</h3>
            <p className="text-slate-700 mb-4">MarketMate is not liable for:</p>
            <ul className="list-disc list-inside text-slate-700 mb-4 space-y-2">
              <li>Issues arising from vendor product quality</li>
              <li>Delivery delays beyond our control</li>
              <li>Losses resulting from misuse of the platform</li>
            </ul>
            <p className="text-slate-700 mb-8">Your use of the platform is at your own risk.</p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">10. Account Suspension or Termination</h3>
            <p className="text-slate-700 mb-4">We may suspend or terminate accounts if:</p>
            <ul className="list-disc list-inside text-slate-700 mb-8 space-y-2">
              <li>These terms are violated</li>
              <li>Fraudulent or suspicious activity is detected</li>
              <li>The platform is misused in any way</li>
            </ul>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">11. Changes to These Terms</h3>
            <p className="text-slate-700 mb-8">
              We may update these Terms & Conditions as the platform evolves. Continued use of MarketMate means you accept any updates.
            </p>

            <h3 className="text-2xl font-bold text-emerald-700 mt-8 mb-4">12. Contact Us</h3>
            <p className="text-slate-700 mb-2">If you have any questions about these terms, please contact us:</p>
            <p className="text-slate-700 text-lg">
              📧 <a href="mailto:support@marketmateconnectltd.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">support@marketmateconnectltd.com</a><br />
              📞 <a href="https://wa.me/2348112560962" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-semibold">+234 811 256 0962</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
